import React from 'react';
import MarkdownView from 'react-showdown';
import { ISectionCell } from '../../Api/Models/Products';
import { extractYouTubeVideoId } from '../Helpers/youtube';
import { resolveStyleProps } from '../Helpers/stylesConfig';

// A cell's own style dictionary, with a `$styleRef` named-style reference resolved (section styles'
// `band` part merged underneath the cell's own values) and the reserved key stripped so it never
// reaches a style prop. Without a ref this returns the cell's own object unchanged.
const cellStyleProps = (cell: ISectionCell) => resolveStyleProps(cell.styleProps, 'band');

// Shared renderer for a section's typed content cells (markdown text, image, video, link). Extracted
// verbatim from HomePage's product-section renderer so product sections, rich-text content sections,
// and any future consumer render cells identically. A cell is one of type 'text' | 'image' | 'video'
// (text = markdown; sourceUrl = media URL; linkUrl = click target; altText; styleProps).

// Renders a single cell's media (YouTube embed / direct image), respecting its styleProps sizing.
export const renderSourceUrl = (cell: ISectionCell) => {
    const cellStyle = cellStyleProps(cell);
    if (cell.sourceUrl && cell.type === 'video') {
        const videoId = extractYouTubeVideoId(cell.sourceUrl ?? '');
        return videoId ? (
            <iframe
                width={cellStyle?.width ?? '100%'}
                height={cellStyle?.height ?? '100%'}
                src={`https://www.youtube.com/embed/${videoId}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
            ></iframe>
        ) : null;
    }
    else if (cell.sourceUrl && cell.type === 'image') {
        if (cell.linkUrl) {
            return (<a href={cell.linkUrl} target="_blank" rel="noopener noreferrer"> <img src={cell.sourceUrl} alt={cell.altText || cell.title || ''} style={{ ...cellStyle, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset' }} /></a>)

        }
        return (<img src={cell.sourceUrl} alt={cell.altText || cell.title || ''} style={{ ...cellStyle, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset' }} />)
    }

    return <></>
}

// Renders a section's header/footer cells (markdown text, image, video, link). Equal-width columns
// (flex: 1 1 0) unless at least one cell pins a width/minWidth/maxWidth via styleProps, in which case
// pinned cells take their fixed width and the rest split the remainder. Returns the array of cell
// divs; the caller supplies the flex row container.
export const renderSectionCells = (cells: ISectionCell[]) => {
    if (!cells || cells.length === 0) return null;
    const anyWidthDefined = cells.some(cell => {
        const s = cellStyleProps(cell) || {};
        return s.width || s.minWidth || s.maxWidth;
    });
    return cells.map((cell, i) => {
        const s = cellStyleProps(cell) || {};
        const heightProps = { height: s.height, minHeight: s.minHeight, maxHeight: s.maxHeight };
        const widthProps = { width: s.width, minWidth: s.minWidth, maxWidth: s.maxWidth };
        let baseFlex;
        if (anyWidthDefined) {
            if (!(s.width || s.minWidth || s.maxWidth)) {
                baseFlex = { flex: '1 1 0', ...heightProps };
            } else {
                baseFlex = { flex: '0 0 auto', ...widthProps, ...heightProps };
            }
        } else {
            baseFlex = { flex: '1 1 0', ...heightProps };
        }
        return (
            <div key={i} style={{ ...baseFlex, ...(cell.type === 'image' ? {} : s) }}>
                {cell.text && <MarkdownView markdown={cell.text} />}
                {renderSourceUrl(cell)}
                {(cell.linkUrl && cell.type !== 'image') && <a href={cell.linkUrl} target="_blank" rel="noopener noreferrer">{cell.linkUrl}</a>}
            </div>
        );
    });
};
